/**
 * CourseCatalogPage — v1 MUI port, modeled on Companies Discovery shape.
 *
 * Top: "Recommended for you" featured row (curated picks for Margot's PMM
 * pivot). Below: department filter chips + searchable browse grid.
 *
 * Pulls from frontend/src/data/foster_courses.json (130 MBA-grad courses).
 * Real catalog API + fit-scoring engine replace this when the academic
 * pipeline lands. Per the spec at instructions/education/CoursePlannerSpec.md.
 *
 * Routes: /courses
 */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { SAGE, CREAM, WARM } from '../theme';
import COURSES from '../data/foster_courses.json';

const F = "'Outfit', sans-serif";
const SERIF = "'Lora', Georgia, serif";

const GOOD = '#3d7a4a';

// Margot's plan state — used for status badges + the right-rail counts.
// Real wiring pulls from the user's plan record; for the demo this is
// curated to match her preferences + dashboard widgets.
const MARGOT_ENROLLED = new Set(['B ECON 501 C', 'I S 500', 'FIN 557']);
const MARGOT_SELECTED = new Set(['MKTG 552', 'MKTG 568']);
const MARGOT_CONSIDERING = new Set(['MKTG 554', 'MKTG 579', 'BUS AN 504']);

const courseStatus = (code) => {
  if (MARGOT_ENROLLED.has(code)) return 'enrolled';
  if (MARGOT_SELECTED.has(code)) return 'selected';
  if (MARGOT_CONSIDERING.has(code)) return 'considering';
  return null;
};

// Recommendations are curated for the demo persona — top picks across the
// PMM/marketing pivot story. Real ranker output replaces this.
const RECOMMENDED_CODES = [
  'MKTG 552', // Consumer Marketing & Brand Strategy
  'MKTG 568', // Pricing Strategy and Analytics
  'MKTG 554', // Strategic Product Management
  'BUS AN 515', // Digital Marketing
];

// Department list pulled from the actual catalog so chips reflect what's
// browseable. Prepended with "All".
const buildDepartments = () => {
  const deps = new Map();
  for (const c of COURSES) {
    const d = c.department || 'Other';
    deps.set(d, (deps.get(d) || 0) + 1);
  }
  const sorted = Array.from(deps.entries()).sort((a, b) => b[1] - a[1]);
  return [{ id: 'all', label: 'All', count: COURSES.length }, ...sorted.map(([d, n]) => ({ id: d, label: d, count: n }))];
};

const Stars = ({ rating }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
    <StarIcon sx={{ fontSize: 12, color: WARM.accent }} />
    <Typography sx={{ fontFamily: F, fontSize: '0.72rem', fontWeight: 700, color: SAGE[700] }}>
      {rating.toFixed(1)}
    </Typography>
  </Box>
);

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const map = {
    enrolled: { label: 'Currently taking', bg: '#e8edf5', color: '#6a7fc4' },
    selected: { label: 'In your plan', bg: SAGE[700], color: CREAM[50] },
    considering: { label: 'Considering', bg: WARM.accent + '22', color: WARM.accent },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 0.85, py: 0.25, borderRadius: 0.75,
        bgcolor: s.bg, color: s.color,
        fontFamily: F, fontSize: '0.6rem', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
      }}
    >
      {status === 'selected' && <CheckCircleIcon sx={{ fontSize: 10 }} />}
      {s.label}
    </Box>
  );
};

// ============================================================================
// Page
// ============================================================================
const CourseCatalogPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeDept, setActiveDept] = useState('all');

  const departments = useMemo(buildDepartments, []);

  const recommended = useMemo(
    () => RECOMMENDED_CODES.map((code) => COURSES.find((c) => c.code === code)).filter(Boolean),
    []
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return COURSES.filter((c) => {
      if (activeDept !== 'all' && c.department !== activeDept) return false;
      if (q) {
        const hay = `${c.code} ${c.title} ${c.description}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [search, activeDept]);

  const planCounts = useMemo(() => ({
    enrolled: MARGOT_ENROLLED.size,
    selected: MARGOT_SELECTED.size,
    considering: MARGOT_CONSIDERING.size,
  }), []);

  return (
    <Box sx={{ bgcolor: CREAM[100], minHeight: '100vh', pb: 6 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[500], fontWeight: 500 }}>
            Academic
          </Typography>
          <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[400], mx: 0.25 }}>›</Typography>
          <Typography sx={{ fontFamily: F, fontSize: '0.78rem', fontWeight: 700, color: SAGE[700] }}>
            Course catalog
          </Typography>
        </Box>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontFamily: SERIF, fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 600, color: SAGE[800], lineHeight: 1.1, mb: 0.5 }}>
              Course catalog
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[500] }}>
              Foster MBA · {COURSES.length} courses across {departments.length - 1} departments
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search by title, code, or instructor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                bgcolor: CREAM[50], borderRadius: 2, fontFamily: F, fontSize: '0.85rem',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: SAGE[400] }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' }, gap: 3 }}>
          {/* Main column */}
          <Box sx={{ minWidth: 0 }}>
            {/* Recommended */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5 }}>
                <Typography sx={{ fontFamily: SERIF, fontSize: '1.15rem', fontWeight: 600, color: SAGE[800], display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <StarIcon sx={{ fontSize: 16, color: WARM.accent }} />
                  Recommended for you
                </Typography>
                <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500], fontStyle: 'italic' }}>
                  Curated against your PMM exploration · {recommended.length}
                </Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                {recommended.map((c) => (
                  <FeaturedCourseCard
                    key={c.code} course={c}
                    status={courseStatus(c.code)}
                    onClick={() => navigate(`/courses/${encodeURIComponent(c.code)}`)}
                  />
                ))}
              </Box>
            </Box>

            {/* Department filter chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {departments.map((d) => {
                const active = activeDept === d.id;
                return (
                  <Chip
                    key={d.id}
                    label={`${d.label} · ${d.count}`}
                    onClick={() => setActiveDept(d.id)}
                    sx={{
                      fontFamily: F, fontSize: '0.78rem', fontWeight: 600,
                      bgcolor: active ? SAGE[700] : CREAM[50],
                      color: active ? CREAM[50] : SAGE[600],
                      border: `1.5px solid ${active ? SAGE[700] : SAGE[200]}`,
                      '&:hover': { bgcolor: active ? SAGE[800] : SAGE[100] },
                    }}
                  />
                );
              })}
            </Box>

            {/* Results grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
              {filtered.length === 0 ? (
                <Typography sx={{ fontFamily: F, fontSize: '0.85rem', color: SAGE[500], fontStyle: 'italic', py: 3, gridColumn: '1 / -1', textAlign: 'center' }}>
                  No courses match. Try a different search or department.
                </Typography>
              ) : (
                filtered.map((c) => (
                  <CourseCard
                    key={c.code} course={c}
                    status={courseStatus(c.code)}
                    onClick={() => navigate(`/courses/${encodeURIComponent(c.code)}`)}
                  />
                ))
              )}
            </Box>
          </Box>

          {/* Right rail — plan-status panel */}
          <Box>
            <Box sx={{ position: 'sticky', top: 16, bgcolor: CREAM[50], borderRadius: 2.5, border: `1.5px solid ${SAGE[200]}`, p: 2.5 }}>
              <Typography sx={{ fontFamily: F, fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: SAGE[500], mb: 1.5 }}>
                Your plan
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <PlanRow color="#6a7fc4" label="Currently taking" count={planCounts.enrolled} />
                <PlanRow color={SAGE[700]}     label="Selected" count={planCounts.selected} />
                <PlanRow color={WARM.accent}   label="Considering" count={planCounts.considering} />
              </Box>
              <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${SAGE[100]}` }}>
                <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500], fontStyle: 'italic', lineHeight: 1.55 }}>
                  Add a course → it lands in your plan as <strong>considering</strong>. Promote to <strong>selected</strong> when you're committed.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const PlanRow = ({ color, label, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
    <Typography sx={{ fontFamily: F, fontSize: '0.82rem', color: SAGE[700], flex: 1 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: F, fontSize: '0.95rem', fontWeight: 700, color: SAGE[800] }}>
      {count}
    </Typography>
  </Box>
);

// Big featured card — recommended row
const FeaturedCourseCard = ({ course, status, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      bgcolor: CREAM[50], borderRadius: 2.5, border: `1.5px solid ${SAGE[200]}`,
      p: 2, cursor: 'pointer',
      display: 'flex', flexDirection: 'column', gap: 0.75,
      transition: 'all 0.18s',
      '&:hover': { borderColor: SAGE[700], boxShadow: `0 4px 16px ${SAGE[200]}80`, transform: 'translateY(-1px)' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      <Typography sx={{ fontFamily: F, fontSize: '0.7rem', fontWeight: 700, color: SAGE[700], bgcolor: SAGE[100], px: 0.85, py: 0.2, borderRadius: 0.75 }}>
        {course.code}
      </Typography>
      <Typography sx={{ fontFamily: F, fontSize: '0.7rem', color: SAGE[500], fontWeight: 500 }}>
        {course.department}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <StatusBadge status={status} />
    </Box>
    <Typography sx={{ fontFamily: SERIF, fontSize: '1.05rem', fontWeight: 500, color: SAGE[800], lineHeight: 1.3 }}>
      {course.title}
    </Typography>
    <Typography sx={{ fontFamily: F, fontSize: '0.78rem', color: SAGE[600], lineHeight: 1.5 }}>
      {course.description.length > 130 ? course.description.slice(0, 127).trim() + '…' : course.description}
    </Typography>
    {course.reviews && course.reviews.length > 0 && (
      <Typography sx={{ fontFamily: SERIF, fontSize: '0.78rem', fontStyle: 'italic', color: SAGE[500], lineHeight: 1.5, mt: 0.5 }}>
        "{course.reviews[0].text.length > 120 ? course.reviews[0].text.slice(0, 117).trim() + '…' : course.reviews[0].text}"
      </Typography>
    )}
  </Box>
);

// Compact card — browse grid
const CourseCard = ({ course, status, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      bgcolor: CREAM[50], borderRadius: 2, border: `1px solid ${SAGE[200]}`,
      p: 1.5, cursor: 'pointer',
      transition: 'all 0.15s',
      '&:hover': { borderColor: SAGE[500], boxShadow: `0 2px 8px ${SAGE[200]}55` },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
      <Typography sx={{ fontFamily: F, fontSize: '0.68rem', fontWeight: 700, color: SAGE[700] }}>
        {course.code}
      </Typography>
      <Typography sx={{ fontFamily: F, fontSize: '0.65rem', color: SAGE[400] }}>·</Typography>
      <Typography sx={{ fontFamily: F, fontSize: '0.66rem', color: SAGE[500], fontWeight: 500 }}>
        {course.credits} cr
      </Typography>
      {course.quartersOffered && course.quartersOffered.length > 0 && (
        <>
          <Typography sx={{ fontFamily: F, fontSize: '0.65rem', color: SAGE[400] }}>·</Typography>
          <Typography sx={{ fontFamily: F, fontSize: '0.66rem', color: SAGE[500], fontWeight: 500 }}>
            {course.quartersOffered.join(', ')}
          </Typography>
        </>
      )}
      <Box sx={{ flex: 1 }} />
      <StatusBadge status={status} />
    </Box>
    <Typography sx={{ fontFamily: SERIF, fontSize: '0.92rem', fontWeight: 500, color: SAGE[800], lineHeight: 1.3, mb: 0.5 }}>
      {course.title}
    </Typography>
    <Typography sx={{ fontFamily: F, fontSize: '0.72rem', color: SAGE[500], lineHeight: 1.45 }}>
      {course.description.length > 100 ? course.description.slice(0, 97).trim() + '…' : course.description}
    </Typography>
  </Box>
);

export default CourseCatalogPage;
